/**
 * 🎨 Editable Button - Button mit Inline Floating Toolbar
 * 
 * Wiederverwendbare Komponente für Buttons mit seamless Inline-Editing
 * Kombiniert Button-Rendering mit ButtonFloatingToolbar
 * 
 * Features:
 * - Inline Text Editing (contentEditable)
 * - Floating Toolbar beim Click/Select
 * - Seamless Style Updates
 * - Responsive Support
 * - Live Preview
 */

import React, { useState, useRef, useEffect } from 'react';
import ButtonFloatingToolbar from './ButtonFloatingToolbar';
import { useDebouncedCallback } from '../../hooks/useDebounce';
import './EditableButton.css';

function EditableButton({
    // Required Props
    buttonText,
    onTextChange,
    isEditMode,
    isSelected,
    onSelect,
    onDeselect, // () => void - Callback zum Deselektieren
    element, // ✅ NEW: Element Objekt für ID (für Custom Event)
    
    // Style Props
    buttonColor,
    buttonTextColor,
    buttonTextSize,
    buttonTextSizeMobile,
    buttonTextWeight,
    buttonTextWeightMobile,
    buttonFontFamily, // ✅ NEU: Font Family für Button Text
    buttonBorderRadius,
    buttonBorderRadiusMobile,
    buttonHeight,
    buttonHeightMobile,
    buttonWidth,
    buttonWidthMobile,
    buttonMaxWidth,
    buttonMaxWidthMobile,
    
    // Advanced Props
    buttonBorderEnabled,
    buttonBorderWidth,
    buttonBorderColor,
    buttonShadowEnabled,
    buttonShadowIntensity,
    buttonShadowDistance,
    buttonShadowOpacity,
    buttonShadowColor,
    buttonGradientEnabled,
    buttonGradientColor1,
    buttonGradientColor2,
    buttonGradientAnimation,
    buttonArrowEnabled,
    buttonArrowDirection,
    buttonArrowIcon, // ✅ NEW: arrow_1, arrow_2, ..., arrow_6
    buttonArrowSize, // ✅ NEW: 12-48px
    buttonArrowInvert, // ✅ NEW: Invert color
    
    // Animation Props
    buttonAnimationEnabled,
    buttonAnimationType, // 'pulse' | 'wiggle' | 'gradient-shift'
    
    // Margins
    buttonMarginTop,
    buttonMarginBottom,
    
    // Alignment Props
    buttonAlignment, // 'left', 'center', 'right'
    buttonAlignmentMobile,
    
    // Callbacks
    onUpdate, // (updates) => void - komplettes Button-Update
    colors = {}, // Brand Colors
    brandFonts = {}, // ✅ NEW: Brand Fonts (globalBigFont, globalSmallFont)
    
    // Additional Props
    placeholder = 'Enter button text...',
    buttonLink = '',
    
    // Responsive
    isMobileLayout = false,
    
    // Styling
    className = '',
    style = {},
    alignSelf = 'flex-start' // 'flex-start', 'center', 'flex-end' - DEPRECATED, use buttonAlignment instead
}) {
    const buttonRef = useRef(null);
    const textSpanRef = useRef(null); // ✅ NEW: Ref für contentEditable span
    const [isEditing, setIsEditing] = useState(false);
    const [localText, setLocalText] = useState(buttonText);
    const [showToolbar, setShowToolbar] = useState(false); // ✅ Internal state für Toolbar visibility
    
    // ⚡ OPTIMISTIC UI: Lokaler State für alle Style-Props (SOFORTIGES Feedback)
    // WICHTIG: Wird NUR beim ersten Mount mit Props initialisiert, danach unabhängig!
    const [localStyles, setLocalStyles] = useState({
        height: buttonHeight,
        heightMobile: buttonHeightMobile,
        borderRadius: buttonBorderRadius,
        borderRadiusMobile: buttonBorderRadiusMobile,
        width: buttonWidth,
        widthMobile: buttonWidthMobile,
        borderWidth: buttonBorderWidth,
        shadowIntensity: buttonShadowIntensity,
        shadowDistance: buttonShadowDistance,
        shadowOpacity: buttonShadowOpacity,
        marginTop: buttonMarginTop,
        marginBottom: buttonMarginBottom,
        textSize: buttonTextSize,
        textSizeMobile: buttonTextSizeMobile,
        textWeight: buttonTextWeight,
        textWeightMobile: buttonTextWeightMobile
    });

    // ⚡ Debounced Parent Update (300ms für Firebase)
    const debouncedParentUpdate = useDebouncedCallback((updates) => {
        if (onUpdate) {
            onUpdate(updates);
        }
    }, 300);

    // ⚡ Handle Style Change (SOFORT lokal + DEBOUNCED zu Parent)
    const handleStyleChange = (updates) => {
        // Check welche Keys updated werden
        const updatedKeys = Object.keys(updates);
        
        // Slider-Keys die optimistisch sein sollen
        const sliderKeys = [
            'buttonHeight', 'buttonHeightMobile',
            'buttonBorderRadius', 'buttonBorderRadiusMobile',
            'buttonWidth', 'buttonWidthMobile',
            'buttonBorderWidth',
            'buttonShadowIntensity', 'buttonShadowDistance', 'buttonShadowOpacity',
            'buttonMarginTop', 'buttonMarginBottom',
            'buttonTextSize', 'buttonTextSizeMobile',
            'buttonTextWeight', 'buttonTextWeightMobile'
        ];
        
        const hasSliderValue = updatedKeys.some(key => sliderKeys.includes(key));
        
        if (hasSliderValue) {
            // ✅ SLIDER VALUES: Optimistic Local State + Debounced Parent
            
            // 1. Update local state (mapping button* keys to local keys)
            const localUpdates = {};
            if (updates.buttonHeight !== undefined) localUpdates.height = updates.buttonHeight;
            if (updates.buttonHeightMobile !== undefined) localUpdates.heightMobile = updates.buttonHeightMobile;
            if (updates.buttonBorderRadius !== undefined) localUpdates.borderRadius = updates.buttonBorderRadius;
            if (updates.buttonBorderRadiusMobile !== undefined) localUpdates.borderRadiusMobile = updates.buttonBorderRadiusMobile;
            if (updates.buttonWidth !== undefined) localUpdates.width = updates.buttonWidth;
            if (updates.buttonWidthMobile !== undefined) localUpdates.widthMobile = updates.buttonWidthMobile;
            if (updates.buttonBorderWidth !== undefined) localUpdates.borderWidth = updates.buttonBorderWidth;
            if (updates.buttonShadowIntensity !== undefined) localUpdates.shadowIntensity = updates.buttonShadowIntensity;
            if (updates.buttonShadowDistance !== undefined) localUpdates.shadowDistance = updates.buttonShadowDistance;
            if (updates.buttonShadowOpacity !== undefined) localUpdates.shadowOpacity = updates.buttonShadowOpacity;
            if (updates.buttonMarginTop !== undefined) localUpdates.marginTop = updates.buttonMarginTop;
            if (updates.buttonMarginBottom !== undefined) localUpdates.marginBottom = updates.buttonMarginBottom;
            if (updates.buttonTextSize !== undefined) localUpdates.textSize = updates.buttonTextSize;
            if (updates.buttonTextSizeMobile !== undefined) localUpdates.textSizeMobile = updates.buttonTextSizeMobile;
            if (updates.buttonTextWeight !== undefined) localUpdates.textWeight = updates.buttonTextWeight;
            if (updates.buttonTextWeightMobile !== undefined) localUpdates.textWeightMobile = updates.buttonTextWeightMobile;
            
            setLocalStyles(prev => ({ ...prev, ...localUpdates }));
            
            // 2. Debounced Parent Update
            debouncedParentUpdate(updates);
        } else {
            // ✅ NON-SLIDER VALUES (Colors, Toggles): Sofort speichern
            if (onUpdate) {
                onUpdate(updates);
            }
        }
    };
    
    // 🎯 Berechne den tatsächlich verwendeten Font (mit Fallback)
    // WICHTIG: Dieser Wert wird sowohl für Rendering als auch für die Toolbar verwendet!
    const actualFontFamily = buttonFontFamily || 'inherit';

    // ✅ FIX: Update local text when prop changes - NUR wenn NICHT gerade editiert wird!
    useEffect(() => {
        if (!isEditing) {
            setLocalText(buttonText);
        }
    }, [buttonText, isEditing]);

    // ✅ Handle Button Click
    const handleButtonClick = (e) => {
        if (isEditMode) {
            // ✅ CRITICAL: Wenn Toolbar bereits offen, lass den Click durch zum Text-Span!
            // Verhindere NUR wenn Toolbar noch geschlossen ist
            if (!showToolbar) {
                e.preventDefault();
                e.stopPropagation();
                
                // 🎯 CRITICAL FIX: Toolbar SOFORT öffnen (nicht warten auf Parent State Update!)
                setShowToolbar(true);
                
                // 🎯 CRITICAL FIX: Fokussiere Text-Span automatisch nach kurzer Verzögerung
                setTimeout(() => {
                    if (textSpanRef.current) {
                        textSpanRef.current.focus();
                        
                        // ✅ Selektiere den gesamten Text für einfaches Überschreiben
                        const selection = window.getSelection();
                        const range = document.createRange();
                        range.selectNodeContents(textSpanRef.current);
                        selection.removeAllRanges();
                        selection.addRange(range);
                    }
                }, 50);
                
                // 🎯 GLOBALES EVENT: Informiere alle TipTap Editoren, dass Button fokussiert wurde
                // Dadurch schließen sich alle offenen TipTap Toolbars automatisch!
                const event = new CustomEvent('tiptap-editor-focus', {
                    detail: { editorId: `button-${element.id || Date.now()}` }
                });
                window.dispatchEvent(event);
                
                // Notify parent dass dieser Button selektiert wurde
                if (onSelect) {
                    onSelect();
                }
            }
            // ✅ Wenn Toolbar bereits offen: Lass Click durch (für Text-Editing)
        } else if (buttonLink) {
            window.open(buttonLink, '_blank');
        }
    };
    
    // ✅ Show/Hide Toolbar basierend auf isSelected
    useEffect(() => {
        setShowToolbar(isSelected);
    }, [isSelected]);
    
    // 🎯 Listen to other editor focus events - Close this toolbar
    useEffect(() => {
        if (!isEditMode) return;
        
        const handleOtherEditorFocus = (event) => {
            const focusedEditorId = event.detail?.editorId;
            const thisButtonId = `button-${element?.id}`;
            
            // Wenn ein ANDERER Editor/Button fokussiert wurde, schließe diese Toolbar
            if (focusedEditorId && focusedEditorId !== thisButtonId) {
                console.log('🎯 [EditableButton] Other editor focused, closing toolbar', {
                    thisButtonId,
                    focusedEditorId
                });
                setShowToolbar(false);
                
                // Deselect Button
                if (onDeselect) {
                    onDeselect();
                }
            }
        };
        
        window.addEventListener('tiptap-editor-focus', handleOtherEditorFocus);
        return () => {
            window.removeEventListener('tiptap-editor-focus', handleOtherEditorFocus);
        };
    }, [isEditMode, element?.id, onDeselect]);
    
    // ✅ Handle Deselect - Close Toolbar
    const handleDeselectButton = () => {
        setShowToolbar(false);
        
        // Optional: Notify parent
        if (onDeselect) {
            onDeselect();
        }
    };

    // ✅ Handle Text Edit (ContentEditable)
    const handleTextEdit = (e) => {
        if (!isEditMode) return;
        const newText = e.currentTarget.textContent;
        // ✅ FIX: Update localText nur für Anzeige, NICHT über React State
        // Dadurch bleibt die Cursor-Position erhalten
    };

    // ✅ Handle Blur - Save Text
    const handleBlur = (e) => {
        const newText = e.currentTarget.textContent;
        setLocalText(newText); // ✅ Update localText beim Blur
        if (newText !== buttonText && onTextChange) {
            onTextChange(newText);
        }
        setIsEditing(false);
    };

    // ⚡ Handle Style Change from Toolbar (wurde weiter oben definiert)
    // handleStyleChange ist jetzt optimistic mit lokalem State + debounced Parent Update

    // 🎨 Calculate Button Styles
    const getButtonStyles = () => {
        // ⚡ OPTIMISTIC: Use local state OR props (lokaler State hat Priorität!)
        const activeHeight = isMobileLayout 
            ? (localStyles.heightMobile ?? buttonHeightMobile ?? localStyles.height ?? buttonHeight ?? 50)
            : (localStyles.height ?? buttonHeight ?? 50);
        const activeBorderRadius = isMobileLayout 
            ? (localStyles.borderRadiusMobile ?? buttonBorderRadiusMobile ?? localStyles.borderRadius ?? buttonBorderRadius ?? 8)
            : (localStyles.borderRadius ?? buttonBorderRadius ?? 8);
        const rawActiveWidth = isMobileLayout 
            ? (localStyles.widthMobile ?? buttonWidthMobile ?? localStyles.width ?? buttonWidth ?? 200)
            : (localStyles.width ?? buttonWidth ?? 200);
        const activeMaxWidth = isMobileLayout ? (buttonMaxWidthMobile ?? buttonMaxWidth ?? 300) : (buttonMaxWidth ?? 300);
        const activeTextSize = isMobileLayout 
            ? (localStyles.textSizeMobile ?? buttonTextSizeMobile ?? localStyles.textSize ?? buttonTextSize ?? 16)
            : (localStyles.textSize ?? buttonTextSize ?? 18);
        const activeTextWeight = isMobileLayout 
            ? (localStyles.textWeightMobile ?? buttonTextWeightMobile ?? localStyles.textWeight ?? buttonTextWeight ?? '600')
            : (localStyles.textWeight ?? buttonTextWeight ?? '600');
        
        // ✅ NEW: Calculate alignSelf from buttonAlignment (responsive)
        const activeAlignment = isMobileLayout ? (buttonAlignmentMobile ?? buttonAlignment ?? 'left') : (buttonAlignment ?? 'left');
        const calculatedAlignSelf = activeAlignment === 'center' ? 'center' : 
                                     activeAlignment === 'right' ? 'flex-end' : 
                                     'flex-start';
        

        // ✅ Width Logic: 'full' = 100%, sonst Pixel-Wert
        const isFullWidth = rawActiveWidth === 'full';
        const activeWidth = isFullWidth ? '100%' : `${rawActiveWidth}px`;

        // Base styles
        let styles = {
            // Background
            background: buttonGradientEnabled 
                ? `linear-gradient(135deg, ${buttonGradientColor1 || '#667eea'} 0%, ${buttonGradientColor2 || '#764ba2'} 100%)`
                : (buttonColor || '#5C80BC'),
            backgroundSize: buttonGradientEnabled && buttonGradientAnimation ? '300% 300%' : 'auto',
            animation: buttonGradientEnabled && buttonGradientAnimation 
                ? 'gradientShift 4s ease-in-out infinite' 
                : 'none',
            
            // Colors & Typography
            color: buttonTextColor || '#FFFFFF',
            fontSize: `${activeTextSize}px`,
            fontWeight: activeTextWeight,
            // ✅ Font Family mit Quotes wenn Leerzeichen enthalten sind
            fontFamily: actualFontFamily !== 'inherit'
                ? (actualFontFamily.includes(' ') ? `"${actualFontFamily}", sans-serif` : `${actualFontFamily}, sans-serif`)
                : 'inherit',
            
            // Dimensions
            width: activeWidth,
            maxWidth: isFullWidth ? 'none' : `${activeMaxWidth}px`,
            height: `${activeHeight}px`,
            borderRadius: `${activeBorderRadius}px`,
            
            // Margins (⚡ OPTIMISTIC)
            marginTop: `${localStyles.marginTop ?? buttonMarginTop ?? 30}px`,
            marginBottom: `${localStyles.marginBottom ?? buttonMarginBottom ?? 0}px`,
            
            // Layout
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            alignSelf: calculatedAlignSelf, // ✅ Use calculated value from buttonAlignment
            
            // Interaction
            cursor: isEditMode ? 'pointer' : (buttonLink ? 'pointer' : 'default'),
            outline: 'none',
            border: 'none',
            
            // Transitions
            transition: 'all 0.3s ease',
            
            // Custom styles
            ...style
        };

        // Border (⚡ OPTIMISTIC)
        const activeBorderWidth = localStyles.borderWidth ?? buttonBorderWidth;
        if (buttonBorderEnabled && activeBorderWidth && activeBorderWidth > 0) {
            styles.border = `${activeBorderWidth}px solid ${buttonBorderColor || buttonColor || '#5C80BC'}`;
        }

        // Shadow (⚡ OPTIMISTIC)
        if (buttonShadowEnabled) {
            const activeShadowIntensity = localStyles.shadowIntensity ?? buttonShadowIntensity ?? 15;
            const activeShadowDistance = localStyles.shadowDistance ?? buttonShadowDistance ?? 8;
            const activeShadowOpacity = localStyles.shadowOpacity ?? buttonShadowOpacity ?? 25;
            const shadowColorWithOpacity = `${buttonShadowColor || '#000000'}${Math.round(activeShadowOpacity * 2.55).toString(16).padStart(2, '0')}`;
            styles.boxShadow = `0 ${activeShadowDistance}px ${activeShadowIntensity}px ${shadowColorWithOpacity}`;
        } else {
            styles.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)';
        }

        // Edit Mode: Selection Outline (nur wenn Toolbar aktiv)
        if (isEditMode && showToolbar) {
            styles.outline = '2px solid rgba(102, 126, 234, 0.7)';
            styles.outlineOffset = '3px';
        }

        return styles;
    };

    // 🎨 Get Animation Class basierend auf animationType
    const getAnimationClass = () => {
        if (!buttonAnimationEnabled || !buttonAnimationType) return '';
        
        const animationClasses = {
            'pulse': 'btn-animation-pulse',
            'wiggle': 'btn-animation-wiggle',
            'gradient-shift': 'btn-animation-gradient-shift'
        };
        
        const className = animationClasses[buttonAnimationType] || '';
        
        // ✅ Debug Log
        // if (className) {
        //     console.log('🎬 [EditableButton] Animation active:', {
        //         buttonAnimationEnabled,
        //         buttonAnimationType,
        //         className
        //     });
        // }
        
        return className;
    };

    // 🎨 Render Arrow Icon basierend auf Direction, Icon Style, Size & Invert
    const renderArrow = () => {
        if (!buttonArrowEnabled) return null;
        
        // ✅ NEW: Icon-basiertes System mit custom SVGs
        const arrowIcon = buttonArrowIcon || 'arrow_1'; // Default: arrow_1
        const arrowSize = buttonArrowSize || 24; // Default: 24px
        const arrowInvert = buttonArrowInvert || false;
        const arrowDirection = buttonArrowDirection || 'right';
        
        // ✅ Rotation Classes basierend auf Direction
        const rotationClasses = {
            left: 'rotate-left',
            right: 'rotate-right',
            up: 'rotate-up',
            down: 'rotate-down'
        };
        
        const rotationClass = rotationClasses[arrowDirection] || 'rotate-right';
        
        return (
            <img 
                src={`/img/${arrowIcon}.svg`}
                alt="Arrow"
                className={`editable-button-arrow-img ${rotationClass} ${arrowInvert ? 'inverted' : ''}`}
                style={{
                    width: `${arrowSize}px`,
                    height: `${arrowSize}px`
                }}
            />
        );
    };

    // 🚫 LIVE VIEW: Einfacher Button ohne Edit-Funktionalität
    if (!isEditMode) {
        return (
            <button
                ref={buttonRef}
                className={`editable-button ${getAnimationClass()} ${className}`}
                style={getButtonStyles()}
                onClick={handleButtonClick}
            >
                {buttonText || placeholder}
                {renderArrow()}
            </button>
        );
    }

    // ✏️ EDIT MODE: Button mit Toolbar
    return (
        <>
            <button
                ref={buttonRef}
                className={`editable-button editable-button-edit-mode ${getAnimationClass()} ${className} ${showToolbar ? 'is-selected' : ''}`}
                style={getButtonStyles()}
                onClick={handleButtonClick}
            >
                <span
                    ref={textSpanRef}
                    contentEditable={isEditMode}
                    suppressContentEditableWarning={true}
                    onInput={handleTextEdit}
                    onBlur={handleBlur}
                    onFocus={(e) => {
                        setIsEditing(true);
                        // ✅ Wenn Span fokussiert wird, stelle sicher dass Toolbar offen ist
                        if (!showToolbar) {
                            setShowToolbar(true);
                            if (onSelect) onSelect();
                        }
                    }}
                    onMouseDown={(e) => {
                        // ✅ CRITICAL: Verhindere dass Button onClick getriggert wird
                        // wenn User direkt auf Text klickt
                        if (showToolbar) {
                            e.stopPropagation();
                        }
                    }}
                    style={{ 
                        outline: 'none', 
                        cursor: isEditMode ? 'text' : 'inherit',
                        pointerEvents: isEditMode ? 'auto' : 'none'
                    }}
                    dangerouslySetInnerHTML={{ __html: localText || placeholder }}
                />
                {renderArrow()}
            </button>

            {/* 🎨 FLOATING TOOLBAR */}
            <ButtonFloatingToolbar
                buttonText={buttonText}
                onTextChange={onTextChange}
                isEditMode={isEditMode}
                isSelected={showToolbar} // ✅ Nutzt internal state
                
                // ⚡ Current Styles (OPTIMISTIC: Local State OR Props)
                backgroundColor={buttonColor}
                textColor={buttonTextColor}
                textSize={localStyles.textSize ?? buttonTextSize}
                textSizeMobile={localStyles.textSizeMobile ?? buttonTextSizeMobile}
                textWeight={localStyles.textWeight ?? buttonTextWeight}
                textWeightMobile={localStyles.textWeightMobile ?? buttonTextWeightMobile}
                fontFamily={actualFontFamily} // ✅ Nutzt berechneten Wert mit Fallback
                borderRadius={localStyles.borderRadius ?? buttonBorderRadius}
                borderRadiusMobile={localStyles.borderRadiusMobile ?? buttonBorderRadiusMobile}
                height={localStyles.height ?? buttonHeight}
                heightMobile={localStyles.heightMobile ?? buttonHeightMobile}
                width={localStyles.width ?? buttonWidth}
                widthMobile={localStyles.widthMobile ?? buttonWidthMobile}
                
                // Advanced (⚡ OPTIMISTIC)
                borderEnabled={buttonBorderEnabled}
                borderWidth={localStyles.borderWidth ?? buttonBorderWidth}
                borderColor={buttonBorderColor}
                shadowEnabled={buttonShadowEnabled}
                shadowIntensity={localStyles.shadowIntensity ?? buttonShadowIntensity}
                shadowDistance={localStyles.shadowDistance ?? buttonShadowDistance}
                shadowOpacity={localStyles.shadowOpacity ?? buttonShadowOpacity}
                shadowColor={buttonShadowColor}
                gradientEnabled={buttonGradientEnabled}
                gradientColor1={buttonGradientColor1}
                gradientColor2={buttonGradientColor2}
                arrowEnabled={buttonArrowEnabled}
                arrowDirection={buttonArrowDirection}
                arrowIcon={buttonArrowIcon} // ✅ NEW: arrow_1, arrow_2, ..., arrow_6
                arrowSize={buttonArrowSize} // ✅ NEW: 12-48px
                arrowInvert={buttonArrowInvert} // ✅ NEW: Invert color
                
                // Animation
                animationEnabled={buttonAnimationEnabled}
                animationType={buttonAnimationType}
                
                // Margins (⚡ OPTIMISTIC)
                marginTop={localStyles.marginTop ?? buttonMarginTop}
                marginBottom={localStyles.marginBottom ?? buttonMarginBottom}
                
                // Alignment
                alignment={buttonAlignment}
                alignmentMobile={buttonAlignmentMobile}
                
                // Link
                buttonLink={buttonLink}
                
                // Callbacks
                onStyleChange={handleStyleChange}
                onDeselect={handleDeselectButton} // ✅ Nutzt internal handler
                colors={colors}
                brandFonts={brandFonts} // ✅ NEW: Brand Fonts für Font Family Section
                buttonRef={buttonRef}
                placeholder={placeholder}
                
                // Responsive
                isMobileLayout={isMobileLayout}
            />
        </>
    );
}

export default EditableButton;

